import {
  addDays,
  addMilliseconds,
  addMonths,
  differenceInCalendarWeeks,
  endOfMonth,
  format,
  getDate,
  getDay,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  parse,
  setDate,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { CalendarEventDatetime } from './calendar-event-datetime.model';
import {
  CalendarEventRecurrence,
  CalendarRecurrenceDayOfWeek,
  CalendarRecurrenceWeekIndex,
} from './calendar-event-recurrence.model';

const DAY_TO_RRULE: Record<CalendarRecurrenceDayOfWeek, string> = {
  sunday: 'SU',
  monday: 'MO',
  tuesday: 'TU',
  wednesday: 'WE',
  thursday: 'TH',
  friday: 'FR',
  saturday: 'SA',
};

const RRULE_TO_DAY: Record<string, CalendarRecurrenceDayOfWeek> = {
  SU: 'sunday',
  MO: 'monday',
  TU: 'tuesday',
  WE: 'wednesday',
  TH: 'thursday',
  FR: 'friday',
  SA: 'saturday',
};

const DAY_TO_WEEK_STARTS_ON: Record<CalendarRecurrenceDayOfWeek, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const INDEX_TO_SETPOS: Record<CalendarRecurrenceWeekIndex, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  last: -1,
};

const SETPOS_TO_INDEX: Record<number, CalendarRecurrenceWeekIndex> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
};

type ParsedRule = {
  timezone?: string;
  dtStartRaw?: string;
  dtEndRaw?: string;
  rrule: Record<string, string>;
};

function formatDateOnly(date: Date, timezone: string) {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
}

function parseDateOnly(dateOnly: string) {
  return parse(dateOnly, 'yyyy-MM-dd', new Date());
}

function parseIcsDateTimeToUtc(value: string, timezone: string): Date | null {
  if (!value) {
    return null;
  }

  const isUTC = value.endsWith('Z');
  const trimmed = isUTC ? value.slice(0, -1) : value;
  const datePart = trimmed.slice(0, 8);
  const timePart = trimmed.length > 8 ? trimmed.slice(9, 15) : '000000';

  if (!datePart || datePart.length !== 8) {
    return null;
  }

  const year = parseInt(datePart.slice(0, 4), 10);
  const month = parseInt(datePart.slice(4, 6), 10);
  const day = parseInt(datePart.slice(6, 8), 10);
  const hour = parseInt(timePart.slice(0, 2), 10);
  const minute = parseInt(timePart.slice(2, 4), 10);
  const second = parseInt(timePart.slice(4, 6), 10);

  if (isUTC) {
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }

  const localString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(
    minute
  ).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  return fromZonedTime(localString, timezone);
}

function formatIcsDateTime(date: Date, timezone: string) {
  return formatInTimeZone(date, timezone, "yyyyMMdd'T'HHmmss");
}

function parseRRule(recurrenceRule: string): ParsedRule | null {
  const source = recurrenceRule.replace(/\r/g, '').trim();
  if (!source) {
    return null;
  }

  const timezoneMatch = source.match(/DTSTART;TZID=([^:\s]+):/);
  const timezone = timezoneMatch?.[1];
  const dtStartRaw = source.match(/DTSTART(?:;TZID=[^:\s]+)?:([0-9TZ]+)/)?.[1];
  const dtEndRaw = source.match(/DTEND(?:;TZID=[^:\s]+)?:([0-9TZ]+)/)?.[1];

  let rruleString = source.match(/RRULE:([^\n ]+)/)?.[1];
  if (!rruleString && source.includes('FREQ=')) {
    const token = source.split(/\s+/).find((part) => part.includes('FREQ='));
    rruleString = token?.replace(/^RRULE:/, '');
  }
  if (!rruleString) {
    return null;
  }

  const rrule = rruleString.split(';').reduce(
    (acc, part) => {
      const [key, value] = part.split('=');
      if (key && value != null) {
        acc[key.toUpperCase()] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  if (!rrule['FREQ']) {
    return null;
  }

  return {
    timezone,
    dtStartRaw,
    dtEndRaw,
    rrule,
  };
}

function getIndexFromSetPos(setPos?: string, byDayToken?: string): CalendarRecurrenceWeekIndex | undefined {
  if (setPos != null) {
    const parsed = parseInt(setPos, 10);
    return SETPOS_TO_INDEX[parsed];
  }

  if (!byDayToken) {
    return undefined;
  }

  const match = byDayToken.match(/^([+-]?\d+)?([A-Z]{2})$/);
  if (!match?.[1]) {
    return undefined;
  }
  const parsed = parseInt(match[1], 10);
  return SETPOS_TO_INDEX[parsed];
}

function getByDayTokens(rrule: Record<string, string>) {
  return (rrule['BYDAY'] ?? '')
    .split(',')
    .map((token) => token.trim().toUpperCase())
    .filter((token) => token.length > 0);
}

function getDaysOfWeek(byDayTokens: string[]): CalendarRecurrenceDayOfWeek[] {
  return byDayTokens
    .map((token) => token.match(/^([+-]?\d+)?([A-Z]{2})$/)?.[2])
    .filter((token): token is string => !!token)
    .map((token) => RRULE_TO_DAY[token])
    .filter((day): day is CalendarRecurrenceDayOfWeek => !!day);
}

function getDateAtLocalTimeInTimezone(dateOnly: Date, localTime: string, timezone: string) {
  const dateKey = format(dateOnly, 'yyyy-MM-dd');
  return fromZonedTime(`${dateKey}T${localTime}`, timezone);
}

function mapRecurrenceTypeFromRRule(rrule: Record<string, string>): CalendarEventRecurrence['pattern']['type'] {
  const freq = rrule['FREQ']?.toUpperCase();
  if (freq === 'DAILY') {
    return 'daily';
  }
  if (freq === 'WEEKLY') {
    return 'weekly';
  }
  if (freq === 'MONTHLY') {
    return rrule['BYMONTHDAY'] ? 'absoluteMonthly' : 'relativeMonthly';
  }
  if (freq === 'YEARLY') {
    return rrule['BYMONTHDAY'] ? 'absoluteYearly' : 'relativeYearly';
  }
  return 'weekly';
}

function nthWeekdayOfMonth(
  year: number,
  monthIndex: number,
  weekday: number,
  index: CalendarRecurrenceWeekIndex
): Date | null {
  if (index === 'last') {
    let cursor = endOfMonth(new Date(year, monthIndex, 1));
    while (getDay(cursor) !== weekday) {
      cursor = addDays(cursor, -1);
    }
    return cursor;
  }

  const offset = INDEX_TO_SETPOS[index] - 1;
  let cursor = new Date(year, monthIndex, 1);
  while (getDay(cursor) !== weekday) {
    cursor = addDays(cursor, 1);
  }
  cursor = addDays(cursor, offset * 7);
  if (getMonth(cursor) !== monthIndex) {
    return null;
  }
  return cursor;
}

function jsDayToCalendarDay(day: number): CalendarRecurrenceDayOfWeek {
  switch (day) {
    case 0:
      return 'sunday';
    case 1:
      return 'monday';
    case 2:
      return 'tuesday';
    case 3:
      return 'wednesday';
    case 4:
      return 'thursday';
    case 5:
      return 'friday';
    default:
      return 'saturday';
  }
}

function addOccurrence(
  occurrences: { start: Date; end: Date }[],
  dateOnly: Date,
  localTime: string,
  timezone: string,
  durationMs: number,
  rangeStart: Date,
  rangeEnd: Date
) {
  const startUtc = getDateAtLocalTimeInTimezone(dateOnly, localTime, timezone);
  const endUtc = addMilliseconds(startUtc, durationMs);

  if ((isAfter(startUtc, rangeStart) || isEqual(startUtc, rangeStart)) && isBefore(startUtc, rangeEnd)) {
    occurrences.push({
      start: startUtc,
      end: endUtc,
    });
  }
}

export function recurrenceToRuleString(
  recurrence: CalendarEventRecurrence | null | undefined,
  eventStart: CalendarEventDatetime,
  eventEnd: CalendarEventDatetime
): string | null {
  if (!recurrence) {
    return null;
  }

  const timezone = recurrence.range.recurrenceTimeZone || eventStart.ianaTimeZone;
  const parts: string[] = [];

  switch (recurrence.pattern.type) {
    case 'daily':
      parts.push('FREQ=DAILY');
      break;
    case 'weekly':
      parts.push('FREQ=WEEKLY');
      if (recurrence.pattern.daysOfWeek?.length) {
        parts.push(`BYDAY=${recurrence.pattern.daysOfWeek.map((day) => DAY_TO_RRULE[day]).join(',')}`);
      }
      break;
    case 'absoluteMonthly':
      parts.push('FREQ=MONTHLY');
      if (recurrence.pattern.dayOfMonth != null) {
        parts.push(`BYMONTHDAY=${recurrence.pattern.dayOfMonth}`);
      }
      break;
    case 'relativeMonthly':
      parts.push('FREQ=MONTHLY');
      if (recurrence.pattern.daysOfWeek?.length) {
        parts.push(`BYDAY=${recurrence.pattern.daysOfWeek.map((day) => DAY_TO_RRULE[day]).join(',')}`);
      }
      if (recurrence.pattern.index) {
        parts.push(`BYSETPOS=${INDEX_TO_SETPOS[recurrence.pattern.index]}`);
      }
      break;
    case 'absoluteYearly':
      parts.push('FREQ=YEARLY');
      if (recurrence.pattern.month != null) {
        parts.push(`BYMONTH=${recurrence.pattern.month}`);
      }
      if (recurrence.pattern.dayOfMonth != null) {
        parts.push(`BYMONTHDAY=${recurrence.pattern.dayOfMonth}`);
      }
      break;
    case 'relativeYearly':
      parts.push('FREQ=YEARLY');
      if (recurrence.pattern.month != null) {
        parts.push(`BYMONTH=${recurrence.pattern.month}`);
      }
      if (recurrence.pattern.daysOfWeek?.length) {
        parts.push(`BYDAY=${recurrence.pattern.daysOfWeek.map((day) => DAY_TO_RRULE[day]).join(',')}`);
      }
      if (recurrence.pattern.index) {
        parts.push(`BYSETPOS=${INDEX_TO_SETPOS[recurrence.pattern.index]}`);
      }
      break;
  }

  if (recurrence.pattern.interval > 1) {
    parts.push(`INTERVAL=${recurrence.pattern.interval}`);
  }

  if (recurrence.pattern.firstDayOfWeek) {
    parts.push(`WKST=${DAY_TO_RRULE[recurrence.pattern.firstDayOfWeek]}`);
  }

  if (recurrence.range.type === 'numbered' && recurrence.range.numberOfOccurrences != null) {
    parts.push(`COUNT=${recurrence.range.numberOfOccurrences}`);
  }

  if (recurrence.range.type === 'endDate' && recurrence.range.endDate) {
    const endLocalTime = formatInTimeZone(eventEnd.utcDateTime, timezone, 'HH:mm:ss');
    const untilUtc = fromZonedTime(`${recurrence.range.endDate}T${endLocalTime}`, timezone);
    const until = formatInTimeZone(untilUtc, 'Etc/UTC', "yyyyMMdd'T'HHmmss");
    parts.push(`UNTIL=${until}Z`);
  }

  const dtStart = formatIcsDateTime(eventStart.utcDateTime, timezone);
  const dtEnd = formatIcsDateTime(eventEnd.utcDateTime, timezone);
  const dtLines =
    timezone === 'Etc/UTC' || timezone === 'UTC'
      ? [`DTSTART:${dtStart}Z`, `DTEND:${dtEnd}Z`]
      : [`DTSTART;TZID=${timezone}:${dtStart}`, `DTEND;TZID=${timezone}:${dtEnd}`];

  return [...dtLines, `RRULE:${parts.join(';')}`].join('\n');
}

export function recurrenceFromRuleString(
  recurrenceRule: string | null | undefined,
  eventStart: CalendarEventDatetime,
  eventEnd: CalendarEventDatetime
): CalendarEventRecurrence | null {
  if (!recurrenceRule) {
    return null;
  }

  const parsed = parseRRule(recurrenceRule);
  if (!parsed) {
    return null;
  }

  const timezone = parsed.timezone || eventStart.ianaTimeZone;
  const rule = parsed.rrule;
  const byDayTokens = getByDayTokens(rule);
  const daysOfWeek = getDaysOfWeek(byDayTokens);
  const primaryByDayToken = byDayTokens[0];

  const type = mapRecurrenceTypeFromRRule(rule);
  const pattern: CalendarEventRecurrence['pattern'] = {
    type,
    interval: Math.max(1, parseInt(rule['INTERVAL'] ?? '1', 10) || 1),
  };

  if (daysOfWeek.length > 0) {
    pattern.daysOfWeek = daysOfWeek;
  }

  if (rule['WKST'] && RRULE_TO_DAY[rule['WKST']]) {
    pattern.firstDayOfWeek = RRULE_TO_DAY[rule['WKST']];
  }

  if (rule['BYMONTHDAY']) {
    pattern.dayOfMonth = parseInt(rule['BYMONTHDAY'].split(',')[0], 10);
  }

  if (rule['BYMONTH']) {
    pattern.month = parseInt(rule['BYMONTH'].split(',')[0], 10);
  }

  const parsedIndex = getIndexFromSetPos(rule['BYSETPOS'], primaryByDayToken);
  if (parsedIndex) {
    pattern.index = parsedIndex;
  }

  const parsedDtStartUtc = parseIcsDateTimeToUtc(parsed.dtStartRaw ?? '', timezone);
  const startDate = formatDateOnly(parsedDtStartUtc ?? eventStart.utcDateTime, timezone);

  const range: CalendarEventRecurrence['range'] = {
    type: 'noEnd',
    startDate,
    recurrenceTimeZone: timezone,
  };

  if (rule['COUNT']) {
    range.type = 'numbered';
    range.numberOfOccurrences = parseInt(rule['COUNT'], 10);
  } else if (rule['UNTIL']) {
    const untilUtc = parseIcsDateTimeToUtc(rule['UNTIL'], timezone);
    if (untilUtc) {
      range.type = 'endDate';
      range.endDate = formatDateOnly(untilUtc, timezone);
    }
  }

  return {
    pattern,
    range,
  };
}

export function expandRecurrenceOccurrences(
  recurrence: CalendarEventRecurrence,
  eventStart: CalendarEventDatetime,
  eventEnd: CalendarEventDatetime,
  rangeStart: Date,
  rangeEnd: Date
) {
  const timezone = recurrence.range.recurrenceTimeZone || eventStart.ianaTimeZone;
  const startDate = parseDateOnly(recurrence.range.startDate);
  const localStartTime = formatInTimeZone(eventStart.utcDateTime, timezone, 'HH:mm:ss');
  const durationMs = Math.max(1, eventEnd.utcDateTime.getTime() - eventStart.utcDateTime.getTime());
  const weekStartsOn = DAY_TO_WEEK_STARTS_ON[recurrence.pattern.firstDayOfWeek ?? 'monday'];

  const untilDate = recurrence.range.endDate ? parseDateOnly(recurrence.range.endDate) : null;
  const rangeEndLocal = parseDateOnly(formatDateOnly(rangeEnd, timezone));
  const maxLocalDate = untilDate && isBefore(untilDate, rangeEndLocal) ? untilDate : rangeEndLocal;

  const interval = Math.max(1, recurrence.pattern.interval || 1);
  const occurrences: { start: Date; end: Date }[] = [];

  let emitted = 0;
  const shouldStop = () =>
    recurrence.range.type === 'numbered' &&
    recurrence.range.numberOfOccurrences != null &&
    emitted >= recurrence.range.numberOfOccurrences;

  const markOccurrence = (dateOnly: Date) => {
    emitted++;
    if (!shouldStop() || emitted <= (recurrence.range.numberOfOccurrences ?? Number.MAX_SAFE_INTEGER)) {
      addOccurrence(occurrences, dateOnly, localStartTime, timezone, durationMs, rangeStart, rangeEnd);
    }
  };

  if (recurrence.pattern.type === 'daily') {
    let cursor = startDate;
    while ((isBefore(cursor, maxLocalDate) || isEqual(cursor, maxLocalDate)) && !shouldStop()) {
      markOccurrence(cursor);
      cursor = addDays(cursor, interval);
    }
    return occurrences;
  }

  if (recurrence.pattern.type === 'weekly') {
    const days = recurrence.pattern.daysOfWeek?.length
      ? recurrence.pattern.daysOfWeek
      : [jsDayToCalendarDay(getDay(startDate))];

    let cursor = startDate;
    while ((isBefore(cursor, maxLocalDate) || isEqual(cursor, maxLocalDate)) && !shouldStop()) {
      const currentWeek = startOfWeek(cursor, { weekStartsOn });
      const startWeek = startOfWeek(startDate, { weekStartsOn });
      const weekDiff = differenceInCalendarWeeks(currentWeek, startWeek, { weekStartsOn });
      const inInterval = weekDiff % interval === 0;
      const currentDay = getDay(cursor);
      const shouldEmit = inInterval && days.some((day) => DAY_TO_WEEK_STARTS_ON[day] === currentDay);

      if (shouldEmit && (isAfter(cursor, startDate) || isEqual(cursor, startDate))) {
        markOccurrence(cursor);
      }
      cursor = addDays(cursor, 1);
    }
    return occurrences;
  }

  if (recurrence.pattern.type === 'absoluteMonthly' || recurrence.pattern.type === 'relativeMonthly') {
    const targetDayOfMonth = recurrence.pattern.dayOfMonth ?? getDate(startDate);
    const relativeDay = recurrence.pattern.daysOfWeek?.[0] ?? jsDayToCalendarDay(getDay(startDate));
    const relativeIndex = recurrence.pattern.index ?? 'first';

    let cursor = new Date(getYear(startDate), getMonth(startDate), 1);
    while ((isBefore(cursor, maxLocalDate) || isEqual(cursor, maxLocalDate)) && !shouldStop()) {
      const monthDiff = (getYear(cursor) - getYear(startDate)) * 12 + (getMonth(cursor) - getMonth(startDate));
      if (monthDiff % interval === 0) {
        let candidate: Date | null = null;
        if (recurrence.pattern.type === 'absoluteMonthly') {
          candidate = setDate(cursor, targetDayOfMonth);
          if (getMonth(candidate) !== getMonth(cursor)) {
            candidate = null;
          }
        } else {
          candidate = nthWeekdayOfMonth(
            getYear(cursor),
            getMonth(cursor),
            DAY_TO_WEEK_STARTS_ON[relativeDay],
            relativeIndex
          );
        }

        if (candidate && (isAfter(candidate, startDate) || isEqual(candidate, startDate))) {
          markOccurrence(candidate);
        }
      }
      cursor = addMonths(cursor, 1);
    }
    return occurrences;
  }

  if (recurrence.pattern.type === 'absoluteYearly' || recurrence.pattern.type === 'relativeYearly') {
    const targetMonth = (recurrence.pattern.month ?? getMonth(startDate) + 1) - 1;
    const targetDayOfMonth = recurrence.pattern.dayOfMonth ?? getDate(startDate);
    const relativeDay = recurrence.pattern.daysOfWeek?.[0] ?? jsDayToCalendarDay(getDay(startDate));
    const relativeIndex = recurrence.pattern.index ?? 'first';

    let year = getYear(startDate);
    const maxYear = getYear(maxLocalDate);
    while (year <= maxYear && !shouldStop()) {
      const yearDiff = year - getYear(startDate);
      if (yearDiff % interval === 0) {
        let candidate: Date | null = null;
        if (recurrence.pattern.type === 'absoluteYearly') {
          candidate = new Date(year, targetMonth, targetDayOfMonth);
          if (getMonth(candidate) !== targetMonth) {
            candidate = null;
          }
        } else {
          candidate = nthWeekdayOfMonth(year, targetMonth, DAY_TO_WEEK_STARTS_ON[relativeDay], relativeIndex);
        }

        if (
          candidate &&
          (isBefore(candidate, maxLocalDate) || isEqual(candidate, maxLocalDate)) &&
          (isAfter(candidate, startDate) || isEqual(candidate, startDate))
        ) {
          markOccurrence(candidate);
        }
      }
      year++;
    }
  }

  return occurrences;
}

export function resolveValidationWindowEnd(
  start: Date,
  validationWindow: {
    unit: 'months' | 'days';
    value: number;
  }
) {
  return validationWindow.unit === 'days'
    ? addDays(start, validationWindow.value)
    : addMonths(start, validationWindow.value);
}
